/**
 * The Personalisation class manages user consent and personalization features.
 * 
 * This class handles:
 * - Checking and responding to user consent for functional cookies.
 * - Storing user-specific data such as the last viewed job, content, and business area.
 * - Fetching and rendering personalized content using AJAX.
 */

import Consent from '../services/consent';
import Cookie from '../services/cookie';
import ajaxHandlerInstance from '../ajax/ajaxHandlerInstance';
import SlickSlider from '../services/slick';
import videojs from 'video.js';
import 'videojs-youtube';

class Personalisation {
    /**
     * Constructor initializes the class, sets up consent management, and
     * begins the personalization logic.
     * 
     * @param {object} personalisation - The personalization data passed from the backend.
     * @param {number|null} personalisation.postID - The ID of the current post.
     * @param {string|null} personalisation.postType - The type of the current post.
     * @param {boolean} [personalisation.businessArea=false] - Flag indicating if the current page is a business area.
     */
    constructor(personalisation) {
        this.consent = new Consent();
        window.consent = this.consent;

        document.dispatchEvent(new Event('consentInitialized'));

        this.ajaxHandler = ajaxHandlerInstance;
        this.personalisation = personalisation;

        this.checkInitialConsent();
        this.setupEventListeners();
    }

    /**
     * Renders all personalisation blocks on the page.
     *
     * This method is responsible for initializing and displaying various
     * personalisation components, such as the dashboard, last viewed job,
     * last viewed content, location-based job slider, saved job icons,
     * and the county dropdown.
     *
     * @returns {void}
     */
    renderBlocks() {
        this.renderDashboard();
        this.renderLastViewedJob();
        this.renderLastViewedContent();
        this.renderLocationJobSlider();

        this.renderSavedJobIcons();
    }

    /**
     * Renders personalisation blocks on the page no 
     * matter the current consent status.
     * 
     * These blocks have additional handling built in to display a
     * no consent given status screen.
     *
     * @returns {void}
     */
    renderBlocksUnconditional() {
        this.renderSavedJobsList();
    } 

    /**
     * Destroys all personalisation blocks on the page.
     *
     * This method clears the content of specific personalisation containers,
     * effectively resetting their state. It is useful for cleaning up components
     * when the user navigates away or before re-rendering.
     *
     * @returns {void}
     */
    destroyBlocks() {
        const dashboardContainer = document.querySelector('.js-personalisation-dashboard');
        if (dashboardContainer) dashboardContainer.innerHTML = '';

        const jobContainer = document.querySelector('.js-personalisation-last-seen-job');
        if (jobContainer) jobContainer.innerHTML = '';

        const contentRestart = document.querySelector('.js-personalisation-restart');
        if (contentRestart) contentRestart.innerHTML = '';

        const contentLocation = document.querySelector('.js-personalisation-slider-job-location');
        if (contentLocation) contentLocation.innerHTML = '';

        // Trigger a re-render 
        this.renderBlocksUnconditional();
    }

    /**
     * Sets cookies to store user personalisation data.
     *
     * This method is responsible for storing information about the user's
     * last viewed job, content, business area, and area of work as cookies.
     * These cookies can be used to provide a personalized experience.
     *
     * @returns {void}
     */
    setCookies() {
        this.storeLastViewedJob();
        this.storeLastViewedContent();
        this.storeLastViewedBusinessArea();
        this.storeLastViewedAreaOfWork();
    }

    /**
     * Deletes personalisation-related cookies.
     *
     * This method removes all cookies associated with user personalisation,
     * including last viewed job, content, business area, area of work, and location.
     * It ensures a clean slate when personalisation data is no longer needed.
     *
     * @returns {void}
     */
    destroyCookies() {
        Cookie.delete('lastViewedJob');
        Cookie.delete('lastViewedContent');
        Cookie.delete('lastViewedBusinessArea');
        Cookie.delete('lastViewedAreaOfWork');
        Cookie.delete('userLocation');
        Cookie.delete('savedJobs');
    }

    /**
     * Checks if functional consent has been granted and performs actions based on consent status.
     * If consent is granted, it stores personalization data in cookies.
     * If consent is revoked, it deletes the stored cookies.
     * 
     * @return {void}
     */
    checkInitialConsent() {
        if (this.consent.hasFunctionalConsent()) {
            console.log('Functional consent has been granted. Performing actions...');

            this.setCookies();
            this.renderBlocks();
        } else {
            console.log('Functional consent has not been granted.');

            this.destroyCookies();
        }

        this.renderBlocksUnconditional();
    }

    /**
     * Sets up event listeners for changes in user consent.
     * If consent is granted or revoked, appropriate actions are taken to manage cookies.
     * 
     * @return {void}
     */
    setupEventListeners() { 
        // Cookie Consent.
        document.addEventListener('consentChanged', (event) => {
            const newConsentStatus = event.detail.newConsentStatus;
            console.log('Consent status changed:', newConsentStatus);
        
            if (newConsentStatus) {
                console.log('Consent has been granted. Performing actions...');
                this.setCookies();
                this.renderBlocks();
            } else {
                console.log('Consent has been revoked.');
                this.destroyCookies();
                this.destroyBlocks();
            }

            this.renderBlocksUnconditional();
        });

        // Save Job Button.
        $(document).on('click', '.js-save-job', (event) => {
            event.preventDefault();
        
            const jobID = $(event.currentTarget).data('jobId');
            if (!jobID) return;
        
            if (!this.consent.hasFunctionalConsent()) return;
        
            this.storeSavedJobs(jobID);
            this.isSavedJob(jobID) ? this.setSavedJobIcon(event.currentTarget) : this.setUnSavedJobIcon(event.currentTarget);
        });

        // Dashboard Video Player.
        $(document).on('click', '.js-dashboard-video__image', (event) => {
            event.preventDefault();
            $('.js-dashboard-video-popup').addClass('active');
        });

        $(document).on('click', '.js-dashboard-video-popup__close', (event) => {
            event.preventDefault();
            $('.js-dashboard-video-popup').removeClass('active');
        });

        // Handle county dropdown selection dynamically
        $(document).on('change', '#county-select', (event) => {
            const selectedCounty = event.target.value;
            this.storeUserLocation(selectedCounty);
        });
    }

    /**
     * Stores the current job post ID in a "last viewed job" cookie if the post type is "job".
     * Overrides the cookie if it is already set.
     * 
     * @return {void}
     */
    storeLastViewedJob() {
        const { postID, postType } = this.personalisation;

        if (postType === 'jobs' && postID) {
            Cookie.set('lastViewedJob', postID.toString(), 7);
            console.log(`Job ID ${postID} has been stored in the "lastViewedJob" cookie.`);
        }
    }

    /**
     * Stores the current content ID (post, page, or custom post type) in a "last viewed content" cookie.
     * Keeps track of the last four unique content IDs, overriding the cookie if necessary.
     * 
     * @return {void}
     */
    storeLastViewedContent() {
        const { postID, postType } = this.personalisation;

        if (postID && postType) {
            // Retrieve the existing content IDs from the cookie
            let lastViewedContent = Cookie.get('lastViewedContent');
            lastViewedContent = lastViewedContent ? JSON.parse(lastViewedContent) : [];

            // Ensure the current content ID is unique
            lastViewedContent = lastViewedContent.filter(id => id !== postID);
            lastViewedContent.unshift(postID); // Add new content ID to the beginning

            // Keep only the last four content IDs
            if (lastViewedContent.length > 10) {
                lastViewedContent = lastViewedContent.slice(0, 4);
            }

            Cookie.set('lastViewedContent', JSON.stringify(lastViewedContent), 7);
            console.log(`Content IDs ${lastViewedContent} have been stored in the "lastViewedContent" cookie.`);
        }
    }

    /**
     * Stores the current page ID in a "last viewed business area" cookie if the post type is "page"
     * and the "businessArea" flag is true. Overrides the cookie if it is already set.
     * 
     * @return {void}
     */
    storeLastViewedBusinessArea() {
        const { postID, postType, businessArea } = this.personalisation;

        if (postType === 'page' && businessArea && postID) {
            Cookie.set('lastViewedBusinessArea', postID.toString(), 7);
            console.log(`Page ID ${postID} has been stored in the "lastViewedBusinessArea" cookie.`);
        }
    }

    /**
     * Stores the current page ID in a "last viewed area of work" cookie if the post type is "page"
     * and the "areaOfWork" flag is true. Overrides the cookie if it is already set.
     * 
     * @return {void}
     */
    storeLastViewedAreaOfWork() {
        const { postID, postType, areaOfWork } = this.personalisation;

        if (postType === 'page' && areaOfWork && postID) {
            Cookie.set('lastViewedAreaOfWork', postID.toString(), 7);
            console.log(`Page ID ${postID} has been stored in the "lastViewedAreaOfWork" cookie.`);
        }
    }

    /**
     * Renders the personalised dashboard section using the last viewed job or area of work.
     * 
     * This function:
     * - Checks if the `.js-personalisation-dashboard` container exists on the page
     * - Retrieves the most recently viewed job or area of work from cookies
     * - Sends an AJAX request to fetch dashboard content based on that post ID
     * - Injects the returned HTML into the dashboard container
     * - Re-renders any saved job icons
     * - Initializes the YouTube video player via video.js if present
     * 
     * If no last viewed item is found or the dashboard container is missing, the function exits early.
     */
    renderDashboard() {
        const dashboardContainer = document.querySelector('.js-personalisation-dashboard');
        if (!dashboardContainer) return; 

        const lastViewed = Cookie.get('lastViewedJob') || Cookie.get('lastViewedAreaOfWork');
        if (!lastViewed) return;   

        this.ajaxHandler.makeRequest(
            'watermill_get_dashboard',
            { postID: lastViewed },
            (response) => {
                if (response.success) {
                    dashboardContainer.innerHTML = response.data;

                    this.renderSavedJobIcons();
                    var player = videojs("js-youtube-video");
                }
            },
            (error) => {
                console.error('Error fetching dashboard:', error);
            }
        );
    }

    /**
    * Fetches and renders the last viewed job using an AJAX request.
    * 
    * - Retrieves the "lastViewedJob" cookie, which contains the job post ID.
    * - Checks if the cookie exists; if not, the method returns early.
    * - Selects the job container in the DOM to inject the fetched job content.
    * - Uses the `AjaxHandler` class to make an AJAX request to fetch the job HTML.
    * - On a successful response, the HTML content is injected into the container.
    * - Logs an error message to the console if the AJAX request fails.
    * 
    * @return {void}
    */
    renderLastViewedJob() {

        const jobContainer = document.querySelector('.js-personalisation-last-seen-job');
        if (!jobContainer) return;

        const lastViewedJob = Cookie.get('lastViewedJob');
        if (!lastViewedJob) return;

        const sliderClassName = `js-${Math.random().toString(36).substring(2, 15)}`;

        this.ajaxHandler.makeRequest(
            'watermill_get_last_viewed_job',
            { 
                jobID: lastViewedJob,
                sliderClassName: sliderClassName,
                },
            (response) => {
                if (response.success) {
                    jobContainer.innerHTML = response.data;

                    // If Job is Live.
                    this.renderSavedJobIcons();

                    // If Job is Expired.
                    const slidesToShow = $(`.${sliderClassName}`).data('slides') || 3;
                    SlickSlider.initialize(`.${sliderClassName}`, slidesToShow);
                }
            },
            (error) => {
                console.error('Error fetching last viewed job:', error);
            }
        );
    }

    /**
     * Fetches and renders the last viewed content using an AJAX request.
     * 
     * - Retrieves the "lastViewedContent" cookie, which contains an array of content IDs.
     * - Checks if the cookie exists; if not, the method returns early.
     * - Parses the content IDs from the cookie and selects the content container in the DOM.
     * - Uses the `AjaxHandler` class to make an AJAX request to fetch the content HTML.
     * - On a successful response, the HTML content is injected into the container.
     * - Logs an error message to the console if the AJAX request fails.
     * 
     * @return {void}
     */
    renderLastViewedContent() {
        const contentContainer = document.querySelector('.js-personalisation-restart');
        if (!contentContainer) return;

        const lastViewedContent = Cookie.get('lastViewedContent');
        if (!lastViewedContent) return;

        const contentIDs = JSON.parse(lastViewedContent);

        this.ajaxHandler.makeRequest(
            'watermill_get_last_viewed_content',
            { 
                contentIDs: contentIDs,
                currentPostID: this.personalisation.postID
            },
            (response) => {
                if (response.success) {
                    contentContainer.innerHTML = response.data;
                }
            },
            (error) => {
                console.error('Error fetching last viewed content:', error);
            }
        );
    }

    /**
     * Fetches location-specific job slider content via AJAX.
     *
     * Uses the userLocation cookie or Cloudflare headers (country and IP) 
     * to determine the user's location and return relevant slider content.
     *
     * @return void Outputs a JSON response with the slider content or an error.
     */
    renderLocationJobSlider() {
        const contentContainer = document.querySelector('.js-personalisation-slider-job-location');
        if (!contentContainer) return;

        // Retrieve the user's location from the cookie, if available
        const userLocation = Cookie.get('userLocation');
    
        // Determine the source of location data
        const locationData = userLocation
            ? { 
                location: userLocation, 
                locationSource: 'cookie' 
              }
            : { 
                location: props.personalisation.location?.ip || null, // Pass the IP
                locationSource: 'cloudflare' 
              };

        const sliderClassName = `js-${Math.random().toString(36).substring(2, 15)}`;
    
        // Make the AJAX request with the location data and its source
        this.ajaxHandler.makeRequest(
            'watermill_get_location_job_slider',
            { ...locationData, sliderClassName },
            (response) => {
                if (response.success) {
                    contentContainer.innerHTML = response.data;
                    
                    // If Job is Live.
                    this.renderSavedJobIcons();
                    this.renderCountyDropdown();

                    // If Job is Expired.
                    SlickSlider.initialize(`.${sliderClassName}`, 2);
                }
            },
            (error) => {
                console.error('Error fetching location slider:', error);
            }
        );
    }

    /**
     * Renders the saved jobs list by fetching job HTML from the backend.
     * 
     * This function:
     * - Reads saved job IDs from the "savedJobs" cookie
     * - Makes an AJAX request to the server to fetch the list of saved jobs
     * - Injects the returned HTML into the `.js-saved-jobs-list` container
     * - Re-renders the saved job icons for visual feedback
     * 
     * If the `.js-saved-jobs-list` container is not found on the page, the function exits early.
     */
    renderSavedJobsList() {
        const contentContainer = document.querySelector('.js-saved-jobs-list');
        if (!contentContainer) return;

        const consentStatus = this.consent.hasFunctionalConsent();

        let savedJobIDs = [];
        try {
            const savedJobs = Cookie.get('savedJobs');
            savedJobIDs = savedJobs ? JSON.parse(savedJobs) : [];
        } catch (e) {
            console.warn('Invalid savedJobs cookie JSON', e);
        }

        this.ajaxHandler.makeRequest(
            'watermill_get_saved_jobs_list',
            { 
                savedJobIDs: savedJobIDs,
                consentStatus: consentStatus
            },
            (response) => {
                if (response.success) {
                    contentContainer.innerHTML = response.data;
                    this.renderSavedJobIcons();
                }
            },
            (error) => {
                console.error('Error fetching Saved Jobs:', error);
            }
        );
    }

    /**
     * Saves the current job ID to a "savedJobs" cookie when the heart icon is clicked.
     * If the cookie already exists, it adds the new job ID to the array of saved job IDs.
     * 
     * @param {number} jobID - The ID of the job to save.
     */
    storeSavedJobs(jobID) {
        let savedJobs = Cookie.get('savedJobs');
        savedJobs = savedJobs ? JSON.parse(savedJobs) : [];
    
        if (savedJobs.includes(jobID)) {
            savedJobs = savedJobs.filter(id => id !== jobID);
            this.setUnSavedJobIcon(document.querySelector(`.js-save-job[data-job-id="${jobID}"]`));
            console.log(`Job ID ${jobID} has been removed from the "savedJobs" cookie.`);
        } else {
            savedJobs.push(jobID);
            this.setSavedJobIcon(document.querySelector(`.js-save-job[data-job-id="${jobID}"]`));
            console.log(`Job ID ${jobID} has been saved in the "savedJobs" cookie.`);
        }
    
        // Update the cookie with the new saved jobs list
        Cookie.set('savedJobs', JSON.stringify(savedJobs), 7);
    }

    /**
     * Render the heart icon with the correct SVG based on the saved status of the job.
     * 
     * @return {void}
     */
    renderSavedJobIcons() {
        const jobElements = document.querySelectorAll('.js-save-job');
        if (!jobElements) return;
    
        jobElements.forEach((element) => {
            const jobID = element.getAttribute('data-job-id');
            if (this.isSavedJob(Number(jobID))) {
                this.setSavedJobIcon(element);
            } else {
                this.setUnSavedJobIcon(element);
            }
        });
    }

    /**
     * Checks if the job ID is saved in the "savedJobs" cookie.
     * 
     * @param {number} jobID - The ID of the job to check.
     * @return {boolean} - Returns true if the job ID is saved, false otherwise.
     */
    isSavedJob(jobID) {
        let savedJobs = Cookie.get('savedJobs');
        savedJobs = savedJobs ? JSON.parse(savedJobs) : [];
        return savedJobs.includes(jobID);
    }

    /**
     * Sets the filled heart SVG on the specified element using vanilla JavaScript.
     * 
     * @param {HTMLElement} element - The element to update with the filled heart SVG.
     */
    setSavedJobIcon(element) {
        element.innerHTML = `<svg role="graphics-symbol"><use xlink:href="#heart-fill"></use></svg>`;
        element.title = 'Remove Saved Job';
    }

    /**
     * Sets the outlined heart SVG on the specified element using vanilla JavaScript.
     * 
     * @param {HTMLElement} element - The element to update with the outlined heart SVG.
     */
    setUnSavedJobIcon(element) {
        element.innerHTML = `<svg role="graphics-symbol"><use xlink:href="#heart"></use></svg>`;
        element.title = 'Save Job';
    }

    /**
     * Updates the userLocation cookie and re-renders the location job slider.
     *
     * @param {string} countyID - The selected county ID.
     * @return {void}
     */
    storeUserLocation(countyID) {
        if (countyID === '') {
            // Delete the userLocation cookie if "Near You" is selected
            Cookie.delete('userLocation');
            console.log('userLocation cookie deleted.');
        } else {
            // Set the userLocation cookie for other selections
            Cookie.set('userLocation', countyID, 7);
            console.log(`userLocation cookie updated to: ${countyID}`);
        }
    
        // Re-run the location job slider
        this.renderLocationJobSlider();
    }

    /**
     * Initializes the county dropdown based on the userLocation cookie.
     *
     * Preselects the dropdown option if a valid userLocation cookie is set.
     *
     * @return {void}
     */
    renderCountyDropdown() {
        const userLocation = Cookie.get('userLocation');
        if (userLocation) {
            const countyDropdown = document.querySelector('#county-select');
            if (countyDropdown) {
                const optionToSelect = countyDropdown.querySelector(`option[value="${userLocation}"]`);
                if (optionToSelect) {
                    optionToSelect.selected = true;
                    console.log(`County dropdown preselected with value: ${userLocation}`);
                }
            }
        }
    }
}

export default Personalisation;